import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_DROP_FLASH, VDJ_MIRROR_NAMES } from './lib/vdjLook.js';

// the mirror modes, cycled on the phrases, in the shared module's numbering
const MIRROR_CYCLE = ['none', 'h', 'quad', 'kaleido'];

// Frames - an endless corridor of neon geometric frames rushing at the
// camera, each turned a little more than the one before, changing shape on
// the bar. The floor is a black mirror. Techno, trance, psytrance, hard
// dance. The VDJ look (lib/vdjLook.js) applies: black, saturated neon with
// white cores, cuts on the bar, 8-beat loops, the beat's RGB kick, mirror
// modes on the phrases, the camera's kick on the bar, the drop's 0.85 flash.
//
// EVERYTHING IS IN THE VERTEX SHADER. One ribbon strip of MAX_SIDES
// segments, instanced: instance i's polygon has aSides sides (its shape
// since its birth), vertex k sits at angle 2 pi k / N, the strip is widened
// along the polygon's radial in SCREEN space (constant pixels, mitred at
// the corners), the depth comes from one uniform uTravel in spacings - a
// frame covers one spacing in exactly one beat, and the one that passes
// the camera is reborn at the far end with the CURRENT shape. The mirror
// modes are instance copies (horizontal 2, quad 4, kaleido 6) folded in ndc.
// The floor's reflection is a second draw of the same geometry with uReflect
// = 1: two draw calls, 48 x copies instances.
//
// THE DECISIVE DETAIL: the shape changes on the bar, but each frame takes
// the new shape at its BIRTH at the far end: for a bar the corridor holds
// both shapes and the new wave travels toward the viewer.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'frames',
  family: 'geometric',
  danceability: 0.90,
  energyLevel: 0.86,
  bpmMin: 120,
  bpmMax: 160,
  movement: 0.90,
  tonality: 0.45,
  prefers: { gridStrength: [0.65, 1.0] },
  enabled: true
};

const DEFAULT_TIER = 2;
const TIER = {
  2: { count: 48, reflection: true },
  1: { count: 48, reflection: true },
  0: { count: 32, reflection: false }
};
const MAX_SIDES = 48;          // a circle
const SPACING = 1.2;           // z between frames; one per beat
const RADIUS = 1.6;
const FLOOR_Y = -2.0;
const NEAR_Z = 0.55;           // the nearest slot sits this far in front of the lens
const WIDTH_PX = [7, 3];       // near, far
const WIDTH_LOW = 0.5;         // x bands.low on top
const SHIMMER = 0.12;          // x bands.high
const CORE = 0.55;             // of the ribbon's half-width is white - under that the bloom's mips bead a thin core
const FAR_DIM = 0.55;          // the far end's brightness; the corridor's vanishing point sits behind the cover
const SHAPES = [3, 4, 6, 3, 5, 4, 8, 6];
const TWIST = { corridor: 0.14, spiral: 0.26, pulse: 0.18, strobe: 0.16, nested: 0.12 };
const TWIST_MAX = 0.50;        // the drop's
const TWIST_BEATS = 8;         // the twist's loop
const TWIST_MONTEE = 1.6;
const MODES = ['corridor', 'spiral', 'pulse', 'strobe', 'nested'];
const SPIRAL_R = 0.55;
const SPIRAL_LOOP = 8;         // beats per turn
const PULSE_GAIN = 0.15;
const NESTED_RINGS = 5;
const NESTED_SIZES = [1.0, 0.78, 0.58, 0.40, 0.24];
const REFLECT_GAIN = 0.30;
const REFLECT_FADE = 0.10;     // per unit of distance
const PASS_FLASH = 0.25;       // of a spacing before the lens, the frame goes white
const DROP_SPIRAL_BARS = 4;
const SAFE_FLOOR = 0.5;
const SYNTH_BPM = 128;
const BEATS_PER_PHRASE = 16;

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(name);
}
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }

// =============================================================================
// SHADER
// =============================================================================

const VERTEX = `
  precision highp float;
  attribute float aK;           // 0..MAX_SIDES, the vertex along the polygon
  attribute float aSide;        // -1 / +1 across the ribbon
  attribute float aInst;        // instance: frame + copy * count
  attribute float aSides;       // the frame's polygon, since its birth
  attribute float aAlt;         // 0/1 colour alternation at birth
  uniform float uCount;
  uniform float uCopies;        // 1, 2, 4 or 6
  uniform float uMirror;        // 0 none, 1 horizontal, 2 quad, 3 kaleido 6
  uniform float uTravel;        // spacings travelled
  uniform float uTwist;         // radians per frame index
  uniform float uMode;          // 0 corridor, 1 spiral, 2 pulse, 3 strobe, 4 nested
  uniform float uSpiralPhase;   // radians
  uniform float uPulse;         // radius factor
  uniform float uParity;        // strobe: the beat's parity
  uniform float uReflect;       // 1: the floor's mirror image
  uniform float uRadius;
  uniform vec2 uWidthPx;        // near, far
  uniform vec2 uResolution;
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  uniform float uRainbow;
  uniform float uWhite;         // the drop: everything white
  uniform float uShimmer;
  uniform float uTime;
  uniform float uGain;
  varying float vSide;
  varying vec3 vColor;
  varying float vSafe;
  varying float vFade;
${SAFE_ZONE_VERTEX_CHUNK}

  vec3 hsv(float h) {
    vec3 k = vec3(1.0, 2.0 / 3.0, 1.0 / 3.0);
    return clamp(abs(fract(vec3(h) + k) * 6.0 - 3.0) - 1.0, 0.0, 1.0);
  }
  vec2 fold(vec2 p, float copy) {
    // p in aspect-corrected ndc
    if (uMirror < 0.5) return p;
    if (uMirror < 1.5) return copy < 0.5 ? p : vec2(-p.x, p.y);
    if (uMirror < 2.5) { if (copy > 0.5 && copy < 1.5) p.x = -p.x; else if (copy > 1.5 && copy < 2.5) p.y = -p.y; else if (copy > 2.5) p = -p; return p; }
    float a = copy * 1.0471976;             // 60 degrees per copy
    if (mod(copy, 2.0) > 0.5) p.y = -p.y;   // alternate reflections
    float c = cos(a), s = sin(a);
    return vec2(p.x * c - p.y * s, p.x * s + p.y * c);
  }

  void main() {
    float i = mod(aInst, uCount);
    float copy = floor(aInst / uCount + 0.001);
    float N = max(3.0, aSides);
    float k = min(aK, N);
    // depth: the frame's slot along the corridor, 0 at the lens
    float depthSlots = uCount;
    float ring = 0.0;
    float idx = i;
    if (uMode > 3.5) { ring = mod(i, ${NESTED_RINGS.toFixed(1)}); idx = floor(i / ${NESTED_RINGS.toFixed(1)}); depthSlots = floor(uCount / ${NESTED_RINGS.toFixed(1)}); }
    float pos = mod(idx - uTravel, depthSlots);
    if (pos < 0.0) pos += depthSlots;
    float z = -(pos * ${SPACING.toFixed(2)} + ${NEAR_Z.toFixed(2)});
    // the polygon
    float rot = idx * uTwist + 0.5236;
    float ang = 6.2831853 * k / N + rot;
    float R = uRadius * uPulse;
    if (uMode > 3.5) R *= (ring < 0.5 ? ${NESTED_SIZES[0].toFixed(2)} : (ring < 1.5 ? ${NESTED_SIZES[1].toFixed(2)} : (ring < 2.5 ? ${NESTED_SIZES[2].toFixed(2)} : (ring < 3.5 ? ${NESTED_SIZES[3].toFixed(2)} : ${NESTED_SIZES[4].toFixed(2)}))));
    vec2 centre = vec2(0.0);
    if (uMode > 0.5 && uMode < 1.5) centre = vec2(cos(idx * 0.35 + uSpiralPhase), sin(idx * 0.35 + uSpiralPhase)) * ${SPIRAL_R.toFixed(2)};
    float off = 0.0;
    if (uMode > 2.5 && uMode < 3.5) off = step(0.5, mod(idx + uParity, 2.0));
    vec3 world = vec3(centre + vec2(cos(ang), sin(ang)) * R, z);
    vec3 centreW = vec3(centre, z);
    if (uReflect > 0.5) { world.y = 2.0 * ${FLOOR_Y.toFixed(1)} - world.y; centreW.y = 2.0 * ${FLOOR_Y.toFixed(1)} - centreW.y; }
    vec4 cv = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
    vec4 cc = projectionMatrix * modelViewMatrix * vec4(centreW, 1.0);
    vec2 aspect = vec2(uResolution.x / uResolution.y, 1.0);
    vec2 nv = cv.xy / max(cv.w, 0.0001) * aspect, nc = cc.xy / max(cc.w, 0.0001) * aspect;
    vec2 radial = normalize(nv - nc + vec2(1e-5, 0.0));
    // the ribbon: constant pixels on the EDGES, mitred at the corners
    float px = mix(uWidthPx.x, uWidthPx.y, pos / depthSlots);
    float halfW = 0.5 * px / cos(3.14159265 / N);
    vec2 p = nv + radial * aSide * halfW * 2.0 / uResolution.y;
    p = fold(p, copy);
    vec4 clip = vec4(p / aspect * cv.w, cv.z, cv.w);
    if (off > 0.5 || cv.w < 0.05) clip = vec4(0.0, 0.0, 2.0, 1.0);
    gl_Position = clip;
    vSide = aSide;
    // colour: two of the palette alternating by birth, or a rainbow along the corridor
    vec3 col = aAlt < 0.5 ? uColorA : uColorB;
    col = mix(col, hsv(fract(pos / depthSlots + uTime * 0.02)), uRainbow);
    // the frame passing the lens goes white; the drop whitens everything
    float passing = 1.0 - smoothstep(0.0, ${PASS_FLASH.toFixed(2)}, pos);
    col = mix(col, vec3(1.0), max(passing, uWhite));
    col *= 1.0 + uShimmer * sin(uTime * 41.0 + i * 1.7);
    vColor = col;
    // dimmer toward the far end, and shared out between the mirror copies
    float dim = mix(1.0, ${FAR_DIM.toFixed(2)}, pos / depthSlots) / sqrt(uCopies);
    vFade = uGain * dim * (uReflect > 0.5 ? ${REFLECT_GAIN.toFixed(2)} * exp(-pos * ${SPACING.toFixed(2)} * ${REFLECT_FADE.toFixed(2)}) : 1.0);
    vSafe = safeZoneAttenuation(vec4(p / aspect, 0.0, 1.0));
  }
`;

const FRAGMENT = `
  precision mediump float;
  varying float vSide;
  varying vec3 vColor;
  varying float vSafe;
  varying float vFade;
  void main() {
    float r = abs(vSide);
    float core = 1.0 - smoothstep(0.0, ${CORE.toFixed(2)}, r);
    float halo = (1.0 - r) * (1.0 - r);
    vec3 col = vec3(1.0) * core + vColor * halo * 0.7;
    float att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, clamp((vSafe - 0.32) / 0.68, 0.0, 1.0));
    gl_FragColor = vec4(col * att * vFade, 1.0);
  }
`;

export class FramesVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    const n = parseInt(urlParam('framesn'), 10);
    this._forcedN = Number.isFinite(n) && n >= 3 && n <= MAX_SIDES ? n : null;
    const m = urlParam('framesmode');
    this._forcedMode = m && MODES.indexOf(m) >= 0 ? m : null;
    const mi = urlParam('framesmirror');
    this._forcedMirror = mi && VDJ_MIRROR_NAMES[mi] != null ? VDJ_MIRROR_NAMES[mi] : null;
    this._post = new VdjPost({ bloomStrength: 0.5, vignette: 0.30 });
    this._chromaFactor = 1;
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);
    camera.position.set(0, 0, 0);
    camera.rotation.set(0, 0, 0);
    camera.updateMatrixWorld();
    const count = this._layout.count;
    this._count = count;

    // --- the strip: MAX_SIDES + 1 vertices along, two across
    const n = MAX_SIDES + 1;
    const pos = new Float32Array(n * 2 * 3), aK = new Float32Array(n * 2), aSide = new Float32Array(n * 2);
    const idx = [];
    for (let k = 0; k < n; k++) {
      aK[k * 2] = k; aK[k * 2 + 1] = k; aSide[k * 2] = -1; aSide[k * 2 + 1] = 1;
      if (k < n - 1) { const a = k * 2; idx.push(a, a + 1, a + 3, a, a + 3, a + 2); }
    }
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aK', new THREE.BufferAttribute(aK, 1));
    geo.setAttribute('aSide', new THREE.BufferAttribute(aSide, 1));
    geo.setIndex(idx);
    const maxInst = count * 6;
    const inst = new Float32Array(maxInst);
    for (let i = 0; i < maxInst; i++) inst[i] = i;
    geo.setAttribute('aInst', new THREE.InstancedBufferAttribute(inst, 1));
    this._sides = new Float32Array(maxInst);
    this._alt = new Float32Array(maxInst);
    this._sidesAttr = new THREE.InstancedBufferAttribute(this._sides, 1).setUsage(THREE.DynamicDrawUsage);
    this._altAttr = new THREE.InstancedBufferAttribute(this._alt, 1).setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aSides', this._sidesAttr);
    geo.setAttribute('aAlt', this._altAttr);
    geo.instanceCount = count;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e3);
    this._geometry = geo;

    this._uniforms = Object.assign({
      uCount: { value: count }, uCopies: { value: 1 }, uMirror: { value: 0 },
      uTravel: { value: 0 }, uTwist: { value: 0 }, uMode: { value: 0 }, uSpiralPhase: { value: 0 },
      uPulse: { value: 1 }, uParity: { value: 0 }, uReflect: { value: 0 }, uRadius: { value: RADIUS },
      uWidthPx: { value: new THREE.Vector2(WIDTH_PX[0], WIDTH_PX[1]) }, uResolution: { value: new THREE.Vector2(1280, 720) },
      uColorA: { value: new THREE.Color(0xff2fb0) }, uColorB: { value: new THREE.Color(0x2fd8ff) }, uRainbow: { value: 0 },
      uWhite: { value: 0 }, uShimmer: { value: 0 }, uTime: { value: 0 }, uGain: { value: 1 }
    }, safeZoneUniforms);
    const mat = (reflect) => new THREE.ShaderMaterial({
      uniforms: Object.assign({}, this._uniforms, { uReflect: { value: reflect } }),
      vertexShader: VERTEX, fragmentShader: FRAGMENT,
      blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide
    });
    this._material = mat(0);
    this._mesh = new THREE.Mesh(geo, this._material);
    this._mesh.frustumCulled = false;
    scene.add(this._mesh);
    if (this._layout.reflection) {
      this._reflectMaterial = mat(1);
      this._reflect = new THREE.Mesh(geo, this._reflectMaterial);
      this._reflect.frustumCulled = false;
      this._reflect.renderOrder = -1;
      scene.add(this._reflect);
    }
    const self = this;
    this._mesh.onBeforeRender = function (renderer) { renderer.getDrawingBufferSize(_size); self._uniforms.uResolution.value.set(_size.x, _size.y); };

    // --- the behaviour
    this._time = 0;
    this._beats = 0;             // beats of music elapsed (the grid when there is one)
    this._travel = 0;            // spacings travelled
    this._lastTravelInt = 0;
    this._rate = 1;
    this._beatPeriod = 60 / SYNTH_BPM;
    this._lastBeatPhase = null;
    this._lastBarPhase = null;
    this._lastPhrasePhase = null;
    this._lastBeatIndex = null;
    this._beatCount = 0;
    this._barCount = 0;
    this._shapeIndex = 0;
    this._currentN = this._forcedN || SHAPES[0];
    this._modeIndex = 0;
    this._mode = this._forcedMode || MODES[0];
    this._mirror = this._forcedMirror != null ? this._forcedMirror : 0;
    this._colorIndex = 0;
    this._palette = [0xff2fb0, 0x2fd8ff, 0xffb02f, 0x7bff2f];
    this._lastPaletteRef = undefined;
    this._rainbow = 0;
    this._pulse = 1;
    this._montee = 0;
    this._dropAt = -1e9;
    this._dropUntil = -1;
    this._lastDropKey = null;
    this._whiteFrames = 0;
    this._comfort = new VdjComfort();
    this._bands = { low: 0.5, high: 0.5 };
    this._kick = { zoom: 1, roll: 0 };
    this._births = 0;
    for (let i = 0; i < maxInst; i++) { this._sides[i] = this._currentN; this._alt[i] = (i % count) & 1; }
    this._sidesAttr.needsUpdate = true; this._altAttr.needsUpdate = true;
    this._applyPalette(null);
    this._applyMirror();
  }

  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length) ? palette.vividColors : [0xff2fb0, 0x2fd8ff, 0xffb02f, 0x7bff2f];
    this._palette = vivid.slice(0, 4);
    while (this._palette.length < 2) this._palette.push(0x2fd8ff);
    this._setColours();
  }
  _setColours() {
    const n = this._palette.length;
    vdjColor(this._palette[this._colorIndex % n], this._uniforms.uColorA.value);
    vdjColor(this._palette[(this._colorIndex + 1) % n], this._uniforms.uColorB.value);
    this._uniforms.uRainbow.value = this._rainbow;
  }
  _applyMirror() {
    const copies = [1, 2, 4, 6][this._mirror] || 1;
    this._uniforms.uMirror.value = this._mirror;
    this._uniforms.uCopies.value = copies;
    this._geometry.instanceCount = this._count * copies;
  }

  // A frame reborn at the far end takes the shape of the moment.
  _birth(i) {
    const copies = [1, 2, 4, 6][this._mirror] || 1;
    for (let c = 0; c < 6; c++) { this._sides[i + c * this._count] = this._currentN; this._alt[i + c * this._count] = this._births & 1; }
    this._births++;
    this._sidesAttr.needsUpdate = true; this._altAttr.needsUpdate = true;
    void copies;
  }
  _allShapes(n) {
    for (let k = 0; k < this._sides.length; k++) this._sides[k] = n;
    this._sidesAttr.needsUpdate = true;
  }

  _onBeat() {
    this._beatCount++;
    this._uniforms.uParity.value = this._beatCount & 1;
    this._pulse = 1 + PULSE_GAIN;
  }
  _onBar() {
    this._barCount++;
    if (this._dropUntil > this._time) return;
    if (!this._forcedN) { this._shapeIndex = (this._shapeIndex + 1) % SHAPES.length; this._currentN = SHAPES[this._shapeIndex]; }
  }
  _onPhrase() {
    if (this._dropUntil > this._time) return;
    if (!this._forcedMode) { this._modeIndex = (this._modeIndex + 1) % MODES.length; this._mode = MODES[this._modeIndex]; }
    this._colorIndex = (this._colorIndex + 1) % this._palette.length;
    this._rainbow = (this._colorIndex % 3 === 2) ? 1 : 0;
    this._setColours();
    if (this._forcedMirror == null) { this._mirror = (this._mirror + 1) % MIRROR_CYCLE.length; this._applyMirror(); }
  }
  _onDrop(director) {
    this._dropAt = this._time;
    this._dropUntil = this._time + DROP_SPIRAL_BARS * 4 * this._beatPeriod;
    if (this._comfort.requestFlash(this._time)) { this._whiteFrames = 1; if (director) director.flash = Math.max(director.flash || 0, VDJ_DROP_FLASH); }
    if (!this._forcedN) { this._shapeIndex = (this._shapeIndex + 1) % SHAPES.length; this._currentN = SHAPES[this._shapeIndex]; }
    this._allShapes(this._currentN);
    if (!this._forcedMode) this._mode = 'spiral';
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    const dt = Math.min(deltaTime, 0.05);
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;
    const bands = director && director.hasReliableGrid && director.bands ? director.bands : null;
    const k = Math.min(1, dt / 0.12);
    this._bands.low += ((bands ? clamp01(bands.low) : 0.5) - this._bands.low) * k;
    this._bands.high += ((bands ? clamp01(bands.high) : 0.5) - this._bands.high) * k;
    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }

    if (!paused) {
      this._time += dt;
      // --- the beat clock: the grid's own index + phase when there is one
      const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
      let beatEdge = false;
      if (beatPhase != null) {
        if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true;
        const bi = profile.beatIndex;
        if (bi != null && this._lastBeatIndex != null) this._beats += (bi - this._lastBeatIndex) + (beatPhase - this._lastBeatPhase);
        else this._beats += dt / this._beatPeriod;
        this._lastBeatIndex = bi != null ? bi : null;
        this._lastBeatPhase = beatPhase;
      } else {
        const before = Math.floor(this._beats);
        this._beats += dt / this._beatPeriod;
        if (Math.floor(this._beats) !== before) beatEdge = true;
      }
      // the montee: a drop announced within 8 bars doubles the speed
      const ev = director && director.nextEvent;
      const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
      this._montee = dropMs != null && dropMs > 0 && dropMs < 32 * this._beatPeriod * 1000 ? 1 : 0;
      const rate = this._montee ? 2 : 1;
      const dBeats = Math.max(0, this._beats - (this._lastBeats != null ? this._lastBeats : this._beats));
      this._lastBeats = this._beats;
      this._travel += dBeats * rate;
      // births: every integer of travel reborns the frame that just passed
      while (this._lastTravelInt < Math.floor(this._travel)) {
        this._lastTravelInt++;
        const slots = this._mode === 'nested' ? Math.floor(this._count / NESTED_RINGS) : this._count;
        const idx = ((this._lastTravelInt - 1) % slots + slots) % slots;
        if (this._mode === 'nested') for (let r = 0; r < NESTED_RINGS; r++) this._birth(idx * NESTED_RINGS + r);
        else this._birth(idx);
      }
      if (beatEdge) this._onBeat();
      const barPhase = director && director.bar != null ? director.bar : profile.barPhase;
      if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) this._onBar(); this._lastBarPhase = barPhase; }
      else if (beatEdge && this._beatCount % 4 === 1) this._onBar();
      const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
      if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase(); this._lastPhrasePhase = phrasePhase; }
      else if (beatEdge && this._beatCount % BEATS_PER_PHRASE === 1 && this._beatCount > 1) this._onPhrase();
      // the drop, on the frame that crosses the mark
      const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
      if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
        const key = Math.round((nowMs + dropMs) / 100);
        if (key !== this._lastDropKey) { this._lastDropKey = key; this._onDrop(director); }
      }
      if (this._dropUntil > 0 && this._time >= this._dropUntil) { this._dropUntil = -1; if (!this._forcedMode) this._mode = MODES[this._modeIndex]; }
      this._pulse += (1 - this._pulse) * Math.min(1, dt / (this._beatPeriod * 0.35));
      // the camera's kick on the bar, the beat's chroma
      const bp = barPhase != null ? barPhase : (this._beats % 4) / 4;
      this._kick = vdjCameraKick(bp, this._barCount);
      const factor = vdjBeatChroma(beatPhase != null ? beatPhase : this._beats % 1, this._beatPeriod);
      this._chromaFactor = factor; this._post.update(factor);
    } else {
      this._kick = { zoom: 1, roll: 0 };
    }
    // THE WHOLE POSE, every frame. scene-manager drifts the camera around a
    // base it re-captures whenever the pose differs from its own last write:
    // writing only rotation.z on top of the drifted orientation made it
    // capture the drifted pose as the new base, and the camera random-walked
    // away (measured at y = 3.9 after thirty frames).
    const cam = this._camera;
    cam.position.set(0, 0, 0);
    cam.rotation.set(0, 0, this._kick.roll);
    cam.zoom = this._kick.zoom; cam.updateProjectionMatrix();

    // --- the uniforms
    const u = this._uniforms;
    const inDrop = this._dropUntil > this._time;
    const twistAmp = inDrop ? TWIST_MAX : (TWIST[this._mode] || 0.14) * (this._montee ? TWIST_MONTEE : 1);
    u.uTravel.value = this._travel;
    u.uTwist.value = twistAmp * Math.sin(this._beats / TWIST_BEATS * Math.PI * 2);
    u.uMode.value = MODES.indexOf(this._mode);
    u.uSpiralPhase.value = (this._beats / SPIRAL_LOOP) * Math.PI * 2;
    u.uPulse.value = this._mode === 'pulse' ? this._pulse : 1;
    const lowW = 1 + WIDTH_LOW * (this._bands.low - 0.5);
    u.uWidthPx.value.set(WIDTH_PX[0] * lowW, WIDTH_PX[1] * lowW);
    u.uShimmer.value = SHIMMER * this._bands.high;
    u.uTime.value = this._time % 100;
    u.uWhite.value = this._whiteFrames > 0 ? 1 : 0;
    if (this._whiteFrames > 0) this._whiteFrames--;
    // the luminance guard: thin ribbons on black; the flash is the only full-screen light
    const est = 0.06 + 0.3 * (this._mode === 'pulse' ? 0.2 : 0) + (director && director.flash ? director.flash * 0.8 : 0);
    u.uGain.value = this._comfort.measure(est, dt);
    if (this._reflect) this._reflect.visible = this._layout.reflection;
  }

  getProbe() {
    const n = this._count, sides = [];
    for (let i = 0; i < n; i++) sides.push(this._sides[i]);
    return {
      t: +this._time.toFixed(3), beats: +this._beats.toFixed(3), travel: +this._travel.toFixed(3), rate: this._montee ? 2 : 1, births: this._births,
      mode: this._mode, mirror: MIRROR_CYCLE[this._mirror], N: this._currentN, twist: +this._uniforms.uTwist.value.toFixed(4), sides,
      kick: { zoom: +this._kick.zoom.toFixed(4), roll: +this._kick.roll.toFixed(4) }, chroma: +this._chromaFactor.toFixed(3), gain: +this._comfort.gain.toFixed(3), lum: +this._comfort.average.toFixed(3), instances: this._geometry.instanceCount
    };
  }

  dispose() {
    this._scene.remove(this._mesh);
    if (this._reflect) this._scene.remove(this._reflect);
    this._geometry.dispose();
    this._material.dispose();
    if (this._reflectMaterial) this._reflectMaterial.dispose();
    this._scene.background = this._prevBackground || null;
    this._camera.zoom = 1; this._camera.rotation.z = 0; this._camera.updateProjectionMatrix();
  }
}

const _size = new THREE.Vector2();
